import { Component, DestroyRef, inject, NgZone, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Router, RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SearchInputComponent } from '../../../../../../components/inputs/search-input/search-input.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import { FeedbackCardComponent } from '../../../../../../components/toasts-and-cards/feedback-card/feedback-card.component';
import { PaginationComponent } from '../../../../../../components/inputs/pagination/pagination.component';
import { MessageService } from '../../../../../../services/message/message.service';
import { LiveRefresh } from '../../../../../../services/live/live-refresh';
import { PromptCardComponent } from '../../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { IPagination } from '../../../../../../types/contactModule';
import { EmptyStateComponent } from '../../../../../../components/ui/empty-state/empty-state.component';
import { StatusComponent } from '../../../../../../components/ui/status/status.component';
import { DataTableComponent } from '../../../../../../components/ui/data-table/data-table.component';
import { SmsTextComponent } from '../../../../../../components/messages/sms-text/sms-text.component';
import { DeliveryComponent } from '../../../../../../components/messages/delivery/delivery.component';
import { MessageRow, toMessageRows } from '../active-messages-table/message-row';

type TArrangementSort =
  | 'messageSubject,asc'
  | 'messageSubject,desc'
  | 'createdAt,desc'
  | 'createdAt,asc'
  | 'modifiedAt,desc'
  | 'modifiedAt,asc';

/** Archived messages rarely change; a minute keeps unarchives and new archives in view. */
const LIVE_MS = 60_000;

@Component({
  selector: 'archived-messages-table',
  standalone: true,
  imports: [
    SearchInputComponent,
    MatTooltipModule,
    PromptCardComponent,
    SortDropdownComponent,
    FeedbackCardComponent,
    PaginationComponent,
    EmptyStateComponent,
    StatusComponent,
    DataTableComponent,
    SmsTextComponent,
    DeliveryComponent,
    RouterLink,
  ],
  templateUrl: './archived-messages.component.html',
  styleUrls: [
    '../active-messages-table/message-table.css',
    './archived-messages.component.css',
  ],
})
export class ArchivedMessagesComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);
  private searchSubject = new Subject<string>();

  router = inject(Router);
  messageService = inject(MessageService);

  messages: any[] = [];
  /** Display values for each message, worked out once per load. */
  rows: MessageRow[] = [];
  selectedMessage: any = {};
  showArchiveModal: boolean = false;
  arrangementSort: TArrangementSort = 'messageSubject,asc';

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'messageSubject',
    nameLabel: 'Subject',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });
  messagePage: number = 0;
  pageSize: number = 10;
  searchTerm: string = '';
  paginationData: IPagination = {
    totalElements: 0,
    pageSize: this.pageSize,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  };
  params: Record<string, unknown> = {
    'archive.equals': true,
  };

  loading: boolean = false;

  get emptyMessage(): string {
    if (this.searchTerm.trim()) return `No messages match “${this.searchTerm.trim()}”.`;
    return 'Nothing archived yet.';
  }

  openArchiveModal(selectedMessage: any) {
    this.selectedMessage = selectedMessage;
    this.showArchiveModal = true;
  }

  closeArchiveModal() {
    this.showArchiveModal = false;
  }

  handleUnArchiveMessage() {
    this.messageService
      .updateMessageStatus({
        id: this.selectedMessage.id,
        uuid: this.selectedMessage.uuid,
        status: 'Completed',
        archive: false,
      })
      .subscribe({
        next: () => {
          this.messageService.fetchMessages(this.updatedParams);
        },
        error: () => {},
        complete: () => {
          this.closeArchiveModal();
        },
      });
  }

  updateMessagesPage(newPage: number) {
    this.messagePage = newPage;
    this.messageService.fetchMessages(this.updatedParams);
  }

  updateSortArrangementCriteria(criteria: TArrangementSort) {
    this.arrangementSort = criteria;
    this.params['sort'] = criteria;
    // A new sort or search is a new list: start from its first page.
    this.messagePage = 0;
    this.messageService.fetchMessages(this.updatedParams);
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    this.messagePage = 0;
    if (term.length < 1) {
      delete this.params['messageSubject.contains'];
    } else {
      this.params['messageSubject.contains'] = term;
    }
    this.searchSubject.next(term);
  }

  get updatedParams() {
    // Spring Pageable reads `size`; `pageSize` is ignored (it always sent 20 rows).
    return { ...this.params, page: this.messagePage, size: this.pageSize };
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.messagePage = 0;
    this.messageService.fetchMessages(this.updatedParams);
  }

  goToMessageDetails(message: any) {
    this.messageService.updateSelectedMessage(message);
    this.router.navigate(['/organization/messages', message?.uuid]);
  }

  /**
   * The subject is the row's link: it navigates itself (and opens a new tab
   * on Cmd/Ctrl-click), so the row's own click handler stays out of it.
   */
  selectFromLink(event: Event, message: any) {
    event.stopPropagation();
    this.messageService.updateSelectedMessage(message);
  }

  goToMessageReport(message: any) {
    this.messageService.updateSelectedMessage(message);
    this.router.navigate(['/organization/messages/report', message?.uuid]);
  }

  ngOnInit() {
    this.messageService.fetchMessages(this.updatedParams);

    this.searchSubject
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.messageService.fetchMessages(this.updatedParams));

    this.messageService.getMessages
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((messages: any[]) => {
        this.messages = messages ?? [];
        this.rows = toMessageRows(this.messages);
      });

    const live = new LiveRefresh(this.zone, {
      refresh: () => this.messageService.fetchMessages(this.updatedParams, { silent: true }),
      interval: () => LIVE_MS,
    });
    live.start();
    this.destroyRef.onDestroy(() => live.stop());

    this.messageService.getActiveMessagesPagination
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: IPagination) => {
        this.paginationData = value;
      });

    this.messageService.getLoadingActiveMessages
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => {
        this.loading = loading;
      });
  }
}
